import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { PlusIcon, ClockIcon, RepeatIcon } from 'lucide-react';
import { MediaView } from '../shared/MediaView';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { kuurLabel, treatmentDuration, treatmentPrice } from '../../utils/catalog';
import { ease } from '../../core/format/motion';
import { euro } from '../../core/format/currency';
import { hasMedia } from '../../core/rules/media';
import { isAllSoldOut } from '../../core/rules/stock';
import { kuurProductId } from '../../core/rules/kuur';
import { shopLabels } from '../../data/shopLabels';
import { usePages } from '../../contexts/SiteContext';
import { useFeatures } from '../../hooks/useFeatures';
import { btnPrimaryLg, btnSecondary, btnSecondaryLg } from '../../ui/styles';
import { track } from '../../utils/analytics';
import type { Treatment } from '../../core/types/catalog';

/**
 * Behandelingen als uitklaplijst: één tegelijk open. Uitgeklapt: het beeld (als er een is, uit
 * het beheer) en daarnaast de uitleg, duur en prijs, de boekknop, en het thuisprotocol erbij.
 */
export function TreatmentList({
  items
}: {
  items: Treatment[];
}) {
  const pages = usePages();
  const {
    openBooking
  } = useOverlayActions();
  const {
    addRoutine,
    add
  } = useCartActions();
  const {
    routineTotals,
    routineForTreatment,
    productById
  } = useCatalog();
  const {
    prepaidKuur
  } = useFeatures();
  const kuurProduct = (slug: string) => productById(kuurProductId(slug));
  const buyKuur = (slug: string) => {
    const p = kuurProduct(slug);
    if (p) add(p);
  };
  const [openSlug, setOpenSlug] = useState<string | null>(items[0]?.slug ?? null);
  const reduce = useReducedMotion();
  // Zelfde getal als in "Waar ik voor sta" op de homepage (Paginateksten › Home › Manifest).
  const homeShare = Math.min(100, Math.max(0, Math.round(pages.home.manifest.homeShare)));
  return <ul className="border-t border-ink/15" data-id="element-8536" data-mp="s917rmc8or2q">
      {items.map(t => {
      const expanded = openSlug === t.slug;
      const panelId = `treatment-${t.slug}`;
      const routine = routineForTreatment(t.slug);
      const totals = routine ? routineTotals(routine) : null;
      const allSoldOut = totals ? isAllSoldOut(totals.items) : false;
      const media = hasMedia(t.media) ? t.media : null;
      const kuur = prepaidKuur ? kuurProduct(t.slug) : undefined;
      return <li key={t.slug} className="border-b border-ink/15" data-id="element-8537" data-mp="59d5vm75qfuq">
            <h3 data-id="element-8538" data-mp="5b6db3plh43s">
              <button type="button" aria-expanded={expanded} aria-controls={panelId} onClick={() => {
            setOpenSlug(expanded ? null : t.slug);
            if (!expanded && t.kuur) track('kuur-bekeken', {
              behandeling: t.slug
            });
          }} className="group flex min-h-[64px] w-full items-baseline gap-4 py-6 text-left sm:gap-8 sm:py-7" data-id="element-8539" data-mp="5bg1clr2x16u">
                <span className="min-w-0 flex-1" data-id="element-8540" data-mp="37l0bqi3z8wls">
                  <span className="block type-title text-ink transition-colors duration-150 ease-soft group-hover:text-clay" data-id="element-8541" data-mp="3clemi7wyqkni">
                    {t.name}
                  </span>
                  <span className="mt-1.5 block text-[14px] text-muted" data-id="element-8542" data-mp="2yo84ifk2cpzc">{t.tagline}</span>
                  {/* Telefoon: duur onder de naam, zodat de prijs rechts genoeg ruimte heeft. */}
                  <span className="mt-1 block text-[13px] text-muted sm:hidden" data-id="element-8543" data-mp="3rcnicyrgl293">{treatmentDuration(t)}</span>
                </span>
                <span className="hidden whitespace-nowrap text-[14px] text-muted sm:block" data-id="element-8544" data-mp="3hkzj4bonugx2">{treatmentDuration(t)}</span>
                <span className="whitespace-nowrap type-name tabular-nums text-ink" data-id="element-8545" data-mp="3mj12kxq00i92">{treatmentPrice(t)}</span>
                {/* Eén plus die een kwartslag draait tot een kruis: zelfde icoon, geen wissel. */}
                <span aria-hidden="true" className={`flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-full border transition-[transform,border-color,color] duration-200 ease-soft ${expanded ? 'rotate-45 border-ink text-ink' : 'border-ink/15 text-muted group-hover:border-ink/40 group-hover:text-ink'}`} data-id="element-8546" data-mp="ivsna5gwkm5k">
                  <PlusIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-8547" data-mp="2cg42u0miuae7" />
                </span>
              </button>
            </h3>

            <AnimatePresence initial={false} data-id="element-8548" data-mp="396mrr7cobp8p">
              {expanded && <motion.div id={panelId} initial={reduce ? {
            opacity: 0
          } : {
            height: 0,
            opacity: 0
          }} animate={reduce ? {
            opacity: 1
          } : {
            height: 'auto',
            opacity: 1
          }} exit={reduce ? {
            opacity: 0
          } : {
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.24,
            ease
          }} className="overflow-hidden" data-id="element-8549" data-mp="35405rn2ooyjf">
                  {/* Twee kolommen (beeld · alles over de behandeling); de boekknop staat direct onder de uitleg, nooit in een smalle zijkolom. */}
                  <div className="grid gap-8 pb-10 md:grid-cols-12 md:gap-10" data-id="element-8550" data-mp="47zaq1jtvfxj">
                    {media && <MediaView media={media} sizes="(min-width: 768px) 30vw, 100vw" className="aspect-[16/10] w-full md:col-span-5 md:aspect-[4/5]" data-id="element-8551" data-mp="94fw4nzn7g6x" />}

                    <div className={media ? 'md:col-span-7' : 'md:col-span-12 lg:max-w-3xl'} data-id="element-8552" data-mp="i7gycdle0n5b">
                      <p className="text-[17px] leading-relaxed text-muted" data-id="element-8553" data-mp="n50czelzz1xs">{t.description}</p>
                      <ul className="mt-5 flex flex-wrap gap-2" aria-label={`Waar ${t.name} op werkt`} data-id="element-8554" data-mp="rw8ocg6w0xdl">
                        {t.focus.map(f => <li key={f} className="rounded-full border border-ink/15 px-3 py-1 text-[13px] text-muted" data-id="element-8555" data-mp="wxo8m618qzux">
                            {f}
                          </li>)}
                        {t.bindweefsel && <li className="rounded-full bg-blush px-3 py-1 text-[13px] text-ink" data-id="element-8556" data-mp="31z500eb3xqa2">Incl. bindweefselmassage</li>}
                      </ul>

                      <dl className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[14px] text-muted" data-id="element-8557" data-mp="2xze27qn5y9w1">
                        <div className="flex items-center gap-2" data-id="element-8558" data-mp="3bqsktwd5s92k">
                          <ClockIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8559" data-mp="36xzjzjkt9kvr" />
                          <dt className="sr-only" data-id="element-8560" data-mp="3lmcf6ec2qzp7">Duur en prijs</dt>
                          <dd data-id="element-8561" data-mp="3goau5nv38qb3">
                            {treatmentDuration(t)} · {treatmentPrice(t)}
                          </dd>
                        </div>
                        {t.kuur && <div className="flex items-center gap-2" data-id="element-8562" data-mp="3pp8r5m73zxk6">
                            <RepeatIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8563" data-mp="3pmwb5ekwvx9r" />
                            <dt className="sr-only" data-id="element-8564" data-mp="20bvsodloh3qz">Kuur</dt>
                            <dd data-id="element-8565" data-mp="1vc9dvzq7bfg2">{kuurLabel(t)}</dd>
                          </div>}
                      </dl>

                      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-8566" data-mp="av9rhmzqhwvz">
                        <button type="button" onClick={() => openBooking(t.slug)} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8567" data-mp="fu4gj6oaohoj">
                          Plan een afspraak<span className="sr-only" data-id="element-8568" data-mp="61f8klrydlxy">: {t.name}</span>
                        </button>
                        {kuur && <button type="button" onClick={() => buyKuur(t.slug)} className={btnSecondaryLg} data-id="element-8569" data-mp="3o6ijin4los6a">
                            Kuur vooruit betalen<span className="sr-only" data-id="element-8570" data-mp="3f758vr4qpxjz">: {t.name}</span>
                          </button>}
                      </div>
                      {kuur && <p className="mt-3 max-w-xl text-[13px] leading-relaxed text-muted" data-id="element-8571" data-mp="3jajx352u1k8i">
                          {kuur.note} Stop je eerder, dan krijg je de niet-gebruikte sessies terug (gedane sessies tegen de losse prijs).
                        </p>}

                      {routine && totals && <div className="mt-7 rounded-2xl bg-sand p-5 sm:p-6" data-id="element-8572" data-mp="8ns357hq2mh">
                          <p className="text-[14px] text-ink" data-id="element-8573" data-mp="3rnboiwd0ukla">
                            {homeShare > 0 ? `Thuis verder: daar haal je ${homeShare}% van je resultaat` : 'Thuis verder'}
                          </p>
                          <div className="mt-3 flex flex-wrap items-center justify-between gap-4" data-id="element-8574" data-mp="3ing9uhk9c7gi">
                            <div className="min-w-0" data-id="element-8575" data-mp="3dnakx95dgz24">
                              <p className="type-name text-ink" data-id="element-8576" data-mp="3dpwwdjvdvypm">{routine.name}</p>
                              <p className="mt-1 text-[13px] text-muted" data-id="element-8577" data-mp="38p930rjon5s8">{totals.items.map(p => p.name).join(' · ')}</p>
                            </div>
                            {allSoldOut ? <span className="text-[13px] text-clay" data-id="element-8578" data-mp="16wugtga3vf60">{shopLabels.soldOutShort}</span> : <button type="button" onClick={() => addRoutine(routine.id)} className={btnSecondary} data-id="element-8579" data-mp="1af9z679vhixy">
                                Toevoegen<span className="sr-only" data-id="element-8580" data-mp="1eg3mayfhqtps">: {routine.name}</span> · {euro(totals.price)}
                              </button>}
                          </div>
                        </div>}
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </li>;
    })}
    </ul>;
}